import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

interface AppSidebarData {
  /** 현재 활성 path 의 prefix (예: '/inventory' 면 /inventory/* 모두 활성) */
  activePath: string;
}

interface NavItem {
  icon: string;
  label: string;
  href: string;
  /** 이 prefix 로 시작하는 모든 path 에서 활성으로 표시 */
  matchPrefix: string;
}

const NAV_ITEMS: NavItem[] = [
  { icon: '🤝', label: '딜',        href: '/deals',      matchPrefix: '/deals' },
  { icon: '🧭', label: '소싱',      href: '/sourcing',   matchPrefix: '/sourcing' },
  { icon: '🏠', label: '대시보드',  href: '/dashboard',  matchPrefix: '/dashboard' },
  // lead / rfq 는 deal/quote 로 흡수 — 메뉴 숨김 (라우트는 deprecated 로 유지)
  // { icon: '📥', label: '리드',      href: '/leads',      matchPrefix: '/leads' },
  // { icon: '📝', label: '견적(RFQ)', href: '/rfq',        matchPrefix: '/rfq' },
  { icon: '📚', label: '카탈로그',  href: '/catalog',    matchPrefix: '/catalog' },
  { icon: '🆕', label: '임시등록',  href: '/catalog-pending', matchPrefix: '/catalog-pending' },
  { icon: '📦', label: '재고',      href: '/inventory',  matchPrefix: '/inventory' },
  { icon: '🏢', label: '거래처',    href: '/suppliers',  matchPrefix: '/suppliers' },
  { icon: '⚙️', label: '설정',      href: '/settings',   matchPrefix: '/settings' },
];

/**
 * 🎯 AppSidebar — 어드민 앱 메인 사이드바 (6섹션)
 *
 * 라우터 인스턴스를 모름. `<a data-link href="...">` 사용 → 라우터가
 * document 레벨에서 클릭 인터셉트.
 *
 * active 표시는 AppShell 이 route:ready 이벤트마다 setActivePath() 호출.
 */
export class AppSidebar extends SolaView<AppSidebarData> {
  constructor() {
    super();
    this.data = { activePath: '/' };
  }

  setActivePath(path: string): void {
    this.data.activePath = path;
    this.rerenderActive();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <aside class="app-sidebar">
        <header class="app-sidebar-header">
          <div class="app-logo">
            <span class="app-logo-mark">⬢</span>
            <span class="app-logo-text">
              HCLTech<br />
              <small>어드민</small>
            </span>
          </div>
        </header>
        <nav class="app-sidebar-nav">
          ${this.renderNavItems()}
        </nav>
        <footer class="app-sidebar-footer">
          <span class="app-build-info">v0.1 · Phase A</span>
        </footer>
      </aside>
    `;
  }

  /** 초기 렌더용 — innerHTML 직접 빌드 */
  private renderNavItems(): string {
    return NAV_ITEMS.map((item) => {
      const active = this.isActive(item) ? ' active' : '';
      return `
        <a class="app-nav-item${active}" data-link href="${item.href}" data-nav="${item.matchPrefix}">
          <span class="app-nav-icon">${item.icon}</span>
          <span class="app-nav-label">${item.label}</span>
        </a>
      `;
    }).join('');
  }

  /** 활성 path 변경 시 — DOM 의 active 클래스만 토글 (재마운트 안 함) */
  private rerenderActive(): void {
    const items = this._rootElement?.querySelectorAll<HTMLElement>('.app-nav-item');
    if (!items) return;
    items.forEach((el) => {
      const prefix = el.dataset.nav || '';
      const isActive = this.matchActive(prefix);
      el.classList.toggle('active', isActive);
    });
  }

  /**
   * '/' 는 정확히 일치할 때만, 나머지는 prefix 매칭.
   * 예: activePath='/inventory/42' → '/inventory' 활성, '/' 비활성
   */
  private matchActive(prefix: string): boolean {
    const path = this.data.activePath;
    if (prefix === '/') return path === '/';
    // 홈('/')은 딜 리스트 — '딜' 메뉴를 활성으로.
    if (prefix === '/deals' && path === '/') return true;
    return path === prefix || path.startsWith(prefix + '/');
  }

  private isActive(item: NavItem): boolean {
    return this.matchActive(item.matchPrefix);
  }
}
